import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { GreenText, LoadingContent, OrderableTableCell } from 'components'
import { getMichiganOutgoingTitleVehicles } from 'controllers/michiganOutgoingTitles/index'
import { MichiganOutgoingTitleVehicleDto } from 'dtos/michiganOutgoingTitles'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, formatDateMoment } from 'utils'

interface AddVehiclesToAGroupDialogProps {
  isLoading: boolean
  michiganOutgoingTitleGuid?: string
  onClose: () => void
  onSave: (values: MichiganOutgoingTitleVehicleDto[]) => void
  open: boolean
}

export default function AddVehiclesToAGroupDialog({ isLoading, michiganOutgoingTitleGuid, onClose, onSave, open }: AddVehiclesToAGroupDialogProps) {
  const [isGettingVehicles, setIsGettingVehicles] = useState<boolean>(false)
  const [vehicles, setVehicles] = useState<MichiganOutgoingTitleVehicleDto[]>([])
  const [selectedVehicles, setSelectedVehicles] = useState<MichiganOutgoingTitleVehicleDto[]>([])

  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof MichiganOutgoingTitleVehicleDto>('importerStockNumber')
  const [includeMichiganOutgoingTitleVehiclesAlreadyInAGroup, setIncludeMichiganOutgoingTitleVehiclesAlreadyInAGroup] = useState<boolean>(false)

  useEffect(() => {
    if (open && michiganOutgoingTitleGuid) {
      setIsGettingVehicles(true)
      getMichiganOutgoingTitleVehicles(michiganOutgoingTitleGuid, includeMichiganOutgoingTitleVehiclesAlreadyInAGroup)
        .then(res => {
          setVehicles(res.value)
        })
        .finally(() => {
          setIsGettingVehicles(false)
        })
    } else {
      setVehicles([])
      setSelectedVehicles([])
    }
  }, [michiganOutgoingTitleGuid, includeMichiganOutgoingTitleVehiclesAlreadyInAGroup, open])

  const onChangeSortByDirection = (propertyName: keyof MichiganOutgoingTitleVehicleDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  return (
    <Dialog open={open} maxWidth='xl' fullWidth>
      <DialogContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item>
            <DialogTitle>Add Vehicles To A Group</DialogTitle>
          </Grid>

          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  checked={includeMichiganOutgoingTitleVehiclesAlreadyInAGroup}
                  disabled={isLoading}
                  onChange={() => setIncludeMichiganOutgoingTitleVehiclesAlreadyInAGroup(!includeMichiganOutgoingTitleVehiclesAlreadyInAGroup)}
                />
              }
              label='Include Michigan Outgoing Title Vehicles Already In A Group'
            />
          </Grid>

          <LoadingContent isLoading={isGettingVehicles}>
            <Grid item xs={12}>
              <Card variant='outlined' sx={{ width: '100%' }}>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={selectedVehicles.length === vehicles.length && selectedVehicles.length !== 0}
                            disabled={isLoading || !vehicles.length}
                            indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== vehicles.length}
                            onChange={() => {
                              if (selectedVehicles.length === vehicles.length) {
                                setSelectedVehicles([])
                              } else if (vehicles) {
                                setSelectedVehicles(vehicles)
                              }
                            }}
                          />
                        </TableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='importerStockNumber'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Importer Stock #
                        </OrderableTableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='vin'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          VIN
                        </OrderableTableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='exporterCompany'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Exporter
                        </OrderableTableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='buyerCompany'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Buyer
                        </OrderableTableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='titleDestinationCompany'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Title Destination
                        </OrderableTableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='year'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Year
                        </OrderableTableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='make'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Make
                        </OrderableTableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='model'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Model
                        </OrderableTableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='titleUploadedDateTime'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Title Uploaded
                        </OrderableTableCell>

                        <OrderableTableCell<MichiganOutgoingTitleVehicleDto>
                          keyName='signedOutboundBOLUploadedDateTime'
                          onChangeOrder={onChangeSortByDirection}
                          order={order}
                          orderBy={orderBy}
                        >
                          Signed BOL
                        </OrderableTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicles?.sort(clientSideOrderBy<MichiganOutgoingTitleVehicleDto>(orderBy, order)).map((vehicle, index) => (
                        <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              color='primary'
                              checked={selectedVehicles.some(selectedVehicle => selectedVehicle.vehicleGuid === vehicle.vehicleGuid)}
                              disabled={isLoading}
                              onChange={() => {
                                if (selectedVehicles.some(selectedVehicle => selectedVehicle.vehicleGuid === vehicle.vehicleGuid)) {
                                  setSelectedVehicles(selectedVehicles.filter(selectedVehicle => selectedVehicle.vehicleGuid !== vehicle.vehicleGuid))
                                } else {
                                  setSelectedVehicles([...selectedVehicles, vehicle])
                                }
                              }}
                            />
                          </TableCell>

                          <TableCell>{vehicle.importerStockNumber}</TableCell>

                          <TableCell>{vehicle.vin}</TableCell>

                          <TableCell>{vehicle.exporterCompany ?? '-'}</TableCell>

                          <TableCell>{vehicle.buyerCompany ?? '-'}</TableCell>

                          <TableCell>{vehicle.titleDestinationCompany ?? '-'}</TableCell>

                          <TableCell>{vehicle.year}</TableCell>

                          <TableCell>{vehicle.make}</TableCell>

                          <TableCell>{vehicle.model}</TableCell>

                          <TableCell>{vehicle.titleUploadedDateTime ? formatDateMoment(vehicle.titleUploadedDateTime) : '-'}</TableCell>

                          <TableCell>{vehicle.signedOutboundBOLUploadedDateTime ? formatDateMoment(vehicle.signedOutboundBOLUploadedDateTime) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </LoadingContent>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Manage Vehicles:**

Vehicles appear in this list if they have a Michigan Title document associated with them but are not yet assigned to any Outgoing Titles Group.

Since a vehicle can have multiple Michigan Title documents and may require more than one title to be sent, the "Include Michigan Outgoing Title Vehicles Already in a Group" toggle is available.- When enabled, vehicles that already belong to at least one Outgoing Titles Group will also be displayed.

**Select Box:**

Clicking the checkbox selects the vehicle in that row.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes.

**SAVE Button:**

Clicking the SAVE button will save any changes.`}
            </GreenText>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
          CANCEL
        </Button>

        <LoadingButton disabled={selectedVehicles.length === 0} loading={isLoading} onClick={() => onSave(selectedVehicles)} variant='contained'>
          SAVE
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
